import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  Grid,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import CustomSelect from '../../../../../common/components/customized/customselect/CustomSelect';
import CustomButton from '../../../../../common/components/buttons/CustomButton';
import GoogleSearchBox from '../../../../../common/components/maps/googlesearchbox/GoogleSearchBox';
import CustomDateTimeCalendar from '../../../../../common/components/customized/customdatetimecalendar/CustomDateTimeCalendar';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from '../../../../../app/redux/hooks';
import {
  PreviewTrip,
  addTripsAction,
  clearPreview,
  driverAction,
  getDriverslist,
  previewTripAction,
  updateTripsAction
} from '../../../redux/reducer/yaantracSlices/mytripsSlice';
import { convertDatetoEpoch, findAddress } from '../../../../../utils/commonFunctions';
import dayjs from 'dayjs';
import constant from '../../../../../utils/constants';
import GoogleMap from '../../../../../common/components/maps/googlemap/GoogleMap';
import CustomIconButton from '../../../../../common/components/buttons/CustomIconButton';
import { AddCircleOutline, Visibility } from '@mui/icons-material';
import {
  updateCenter,
  updateZoom
} from '../../../../../common/redux/reducer/commonSlices/mapSlice';
import { decode } from '@googlemaps/polyline-codec';
import { updatePolyline } from '../../../../../common/redux/reducer/commonSlices/polylineSlice';
import GooglePolyline from '../../../../../common/components/maps/googlepolyline/GooglePolyline';
import GoogleTripDirections from '../../../../../common/components/maps/googledirection/GoogleTripDirections';
import './AddTrip.scss';
import LoginRedirect from '../../../../../common/components/loginredirect/LoginRedirect';

interface AddTripProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  data: string[];
  row: any;
  setUpdateData?: any;
  setTab?: any;
  setPageSize?: any;
  setPageNo?: any;
  setsearchValue?: any;
  setChildState: React.Dispatch<React.SetStateAction<any>>;
  children: ReactNode;
  setTripType?: any;
}

interface RouteMeasure {
  origin: string | null;
  destination: string | null;
  distance: number | null;
  duration: number | null;
}
interface DriverResponse {
  id: string;
  name: string;
}

// Break Hours props type:
type BreakHours = {
  break: string;
  id: string;
  label: string;
};

type PayloadAction<T> = {
  payload: T;
  type: string;
};

type DriverList = { id: string; label: string }[];

const AddTrip: React.FC<AddTripProps> = ({
  isOpen,
  setIsOpen,
  setTab,
  data,
  row,
  setUpdateData,
  setPageSize,
  setPageNo,
  setChildState,
  children,
  setsearchValue,
  setTripType
}) => {
  const [sourceAddress, setSourceAddress] = useState<any>(null);
  const [destinationAddress, setDestinationAddress] = useState<any>(null);
  const [tripMarker, setTripMarker] = useState<any>([]);
  const [minDateTime, setMinDateTime] = useState<any>();
  const [maxDateTime, setMaxDateTime] = useState<any>();
  const [waypoints, setWaypoints] = useState<any>([]);
  const [source, setSource] = useState('');
  const [destination, setDestination] = useState('');
  const [isShowCalendar, setIsShowCalendar] = useState<boolean>(false);
  const [waypointDisable, setWaypointDisable] = useState(false);
  const [actualToDate, setActualToDate] = useState<any>(null);
  const [breakHour, setBreakHour] = useState<BreakHours | null>(null);
  const [driverOptionData, setDriverOptionData] = useState([]);
  const [isWayPoint, setIsWayPoint] = useState<boolean>(false);
  const [fromDate, setFromDate] = useState(
    row?.tripID && dayjs.unix(row?.startTimestamp)
  );
  const [toDate, setToDate] = useState(row?.tripID && dayjs.unix(row?.endTimestamp));
  type DriverList = { id: string; label: string }[];
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMatchesBelowSM = useMediaQuery(theme.breakpoints.down('sm'));

  const schema = yup.object().shape({
    vehicleNumber: yup.string().required('Select vehicle'),
    driverId: yup.string().required('Select a driver'),
    sourceAddress: yup.object().shape({
      source: yup.string().required('Enter the source address'),
      startLat: yup.number().required('Enter the source address'),
      startLng: yup.number().required('Enter the source address')
    }),
    destinationAddress: yup.object().shape({
      destination: yup.string().required('Enter the destination address'),
      endLat: yup.number().required('Enter the destination address'),
      endLng: yup.number().required('Enter the destination address')
    }),

    waypointDetails: yup.array().of(
      yup.object().shape({
        waypointAddress: yup.string().required('Enter the  waypoint address'),
        waypointLat: yup.number().required('Enter the waypoint address'),
        waypointLng: yup.number().required('Enter the waypoint address'),
        waypointColor: yup.string().required('Enter the waypoint address'),
        waypointRadius: yup.number().required('Enter the waypoint address')
      })
    ),

    startDate: yup
      .object()
      .required('Enter start date and time')
      .test('not-empty', 'Enter start date and time', function (value) {
        return Object.keys(value).length !== 0;
      }),
    endDate: yup
      .object()
      .required('Enter end date and time')
      .test('not-empty', 'Enter end date and time', function (value) {
        return value && Object.keys(value).length !== 0;
      })
      .test(
        'end-after-start',
        'End date must be after start date',
        function (value, context) {
          const startDate = context.parent.startDate;
          if (
            !value ||
            !startDate ||
            Object.keys(value).length === 0 ||
            Object.keys(startDate).length === 0
          ) {
            return true;
          }
          const start = new Date((startDate as any).$d || startDate);
          const end = new Date((value as any).$d || value);
          return end.getTime() > start.getTime();
        }
      ),

    actualEndDate: yup
      .object()
      .required('Enter actual end date and time')
      .test('not-empty', 'Enter actual end date and time', function (value) {
        return Object.keys(value).length !== 0;
      })
  });

  const {
    control,
    handleSubmit,
    setValue,
    clearErrors,
    getValues,
    setError,
    reset,
    trigger,
    resetField,
    formState: { errors }
  } = useForm<any>({
    resolver: yupResolver(schema)
  });

  const { fields, append, remove } = useFieldArray({ name: 'waypointDetails', control });

  const addData = useAppSelector(state => state.addTripSlice);

  const updateData = useAppSelector(state => state.updateTripSlice);

  const { deviceList } = useAppSelector(state => state.devicelist);

  const { data: driverData }: { data: DriverData } = useAppSelector(
    state => state.drivertripsdata
  );
  const { data: previewTripData, isLoading: isLoadingPreview } = useAppSelector(
    state => state.previewTrip
  );
  const { paths } = useAppSelector((state: any) => state.polyline);
  type DriverData = string[] | null;

  // changes of driver update and add
  const onsubmit = async (params: any) => {
    const startDate = dayjs(params.startDate);
    const endDate = dayjs(params.endDate);
    const actualEndDate = dayjs(params.actualEndDate);

    if (!startDate.isValid() || !endDate.isValid()) {
      return;
    }

    const startTimestamp = startDate.unix();
    const endTimestamp = endDate.unix();
    const estimatedEndTime = actualEndDate.unix();

    if (isNaN(startTimestamp) || isNaN(endTimestamp)) {
      return;
    }
    const payload = {
      driverId: params.driverId,
      tripType: row?.tripType,
      vehicleNumber: params.vehicleNumber,
      startGeoZoneColor: '#19A7CE',
      startGeoZoneRadius: null,
      startTimestamp,
      endGeoZoneColor: '#19A7CE',
      endGeoZoneRadius: null,
      endTimestamp,
      estimatedEndTime,
      source: params.sourceAddress?.source,
      startLat: params.sourceAddress?.startLat,
      startLng: params.sourceAddress?.startLng,
      destination: params.destinationAddress?.destination,
      endLat: params.destinationAddress?.endLat,
      endLng: params.destinationAddress?.endLng,
      waypointDetails: params.waypointDetails || []
    };

    let result;
    if (row?.tripID) {
      result = await dispatch(updateTripsAction({ ...payload, tripID: row.tripID }));
      if (result?.type === updateTripsAction.fulfilled.type) {
        setPageNo(1);
        setPageSize(5);
        setTab(1);
        setTripType('Scheduled');
        handleClose();
        reset();
      }

      // setsearchValue('search', '');
    } else {
      result = await dispatch(addTripsAction(payload));

      if (result?.type === addTripsAction.fulfilled.type) {
        setTab(1);
        setTripType('Scheduled');
        setPageNo(1);
        setPageSize(5);
        handleClose();
        reset();
      }
      // setsearchValue('search', '');
    }
  };

  // settab
  const getPreview = async () => {
    const {
      vehicleNumber,
      sourceAddress: { source, startLat, startLng },
      destinationAddress: { destination, endLat, endLng },
      waypointDetails = []
    } = getValues();

    if (!vehicleNumber) {
      setError('vehicleNumber', { message: 'Select the vehicle' });
      return;
    }

    const waypoints = waypointDetails?.filter((item: any) =>
      Boolean(item?.waypointAddress)
    );

    const previewPayload: any = {
      vehicleNumber,
      source,
      startLat,
      startLng,
      destination,
      endLat,
      endLng,
      waypointDetails: waypoints
    };
    await dispatch(previewTripAction(previewPayload));
  };

  const vehicleList = data.map((value: string) => ({
    id: value,
    label: value.toUpperCase()
  }));

  const driverDropdown =
    (driverData as any)?.length > 0
      ? (driverData as any)?.map((value: any) => ({
          id: value?.driverId,
          label: `${value?.driverName?.toUpperCase()}${
            value?.totalBreakDuration ? `(${value?.totalBreakDuration})` : ''
          }`,
          break: value?.totalBreakDuration
        }))
      : [];

  const AllvehicleList = deviceList.map((value: string) => ({
    id: value,
    label: value.toUpperCase()
  }));

  const filter = AllvehicleList?.filter((item: any) => item.id === row?.vehicleNumber);

  const filterData = vehicleList?.filter(
    (item: { id: string }) => item?.id === filter[0]?.id
  );

  const UpdateVehicleList = !filterData ? [...filter, ...vehicleList] : [...vehicleList];

  const handleClose = () => {
    setTripMarker([]);
    dispatch(updatePolyline([]));
    setIsOpen(false);
    setUpdateData([]);
    if (fromDate) {
      setValue('startDate', {});
      setFromDate(null);
    }
    if (toDate) {
      setValue('endDate', {});
      setToDate(null);
    }
    if (row?.length === 0) {
      setMaxDateTime(null);
      setMinDateTime(null);
    }
    clearErrors('vehicleNumber');
    setDestination('');
    clearErrors('endDate');
    clearErrors('startDate');
    setSource('');
    setValue('waypointDetails', []);
    setWaypoints([]);
  };

  const clearStartAndEndDate = () => {
    setIsShowCalendar(false);
    if (fromDate) {
      setValue('startDate', {});
      clearErrors('startDate');
      setFromDate(null);
    }
    if (toDate) {
      setValue('endDate', {});
      clearErrors('endDate');
      setToDate(null);
    }
    if (actualToDate) {
      setValue('actualEndDate', {});
      clearErrors('actualEndDate');
      setActualToDate(null);
    }
    setValue('driverId', '');
    setDriverOptionData([]);
    dispatch(updatePolyline([]));
  };

  const address = async (lat: any, lng: any) => {
    let fAdd = findAddress(lat, lng);
    return fAdd;
  };

  const actualEndTime = (value: string, breakHour: BreakHours | null) => {
    if (value !== undefined) {
      const endtime = new Date(value);

      if (breakHour === null) {
        const convertedEpoch = dayjs.unix(convertDatetoEpoch(value) / 1000);

        setActualToDate(convertedEpoch);
        setValue('actualEndDate', convertedEpoch);
      } else {
        // Convert "hh:mm" string to numeric hours and minutes
        const [hours, minutes] = breakHour?.break?.split(':').map(Number);

        // Add hours and minutes to the existing endtime
        endtime.setHours(endtime?.getHours() + hours);
        endtime.setMinutes(endtime?.getMinutes() + minutes);

        // Convert to epoch time
        const epochMillis = endtime?.getTime();
        const actualTime = Math?.floor(epochMillis / 1000);

        // Set values using dayjs
        const actualDate = dayjs.unix(actualTime);
        setActualToDate(actualDate);
        setValue('actualEndDate', actualDate);
      }
    }
  };

  const handleMarkerDrag = async (e: any, marker: any) => {
    if (marker === 'source') {
      setSourceAddress({
        geometry: {
          location: {
            lat: () => {
              return e?.latLng?.lat();
            },
            lng: () => {
              return e?.latLng?.lng();
            }
          }
        },
        formatted_address: address(e?.latLng?.lat(), e?.latLng?.lng())
      });
    } else if (marker === 'destination') {
      setDestinationAddress({
        geometry: {
          location: {
            lat: () => {
              return e?.latLng?.lat();
            },
            lng: () => {
              return e?.latLng?.lng();
            }
          },
          formatted_address: address(e?.latLng?.lat(), e?.latLng?.lng())
        }
      });
    } else {
      const waypointAddress = await address(e?.latLng?.lat(), e?.latLng?.lng());
      let updatedMarkers = waypoints?.map((item: any) =>
        item?.waypointName === marker
          ? {
              ...item,
              waypointLat: e?.latLng?.lat(),
              waypointLng: e?.latLng?.lng(),
              waypointAddress: waypointAddress
            }
          : item
      );
      setWaypoints(updatedMarkers);
    }
  };

  // add and update
  // changes
  useEffect(() => {
    if (row?.tripID) {
      setValue('vehicleNumber', row?.vehicleNumber);
      setValue('startDate', dayjs.unix(row?.startTimestamp));
      setValue('endDate', dayjs.unix(row?.endTimestamp));
      setValue('waypointDetails', row?.waypointDetails);
      setValue('driverId', row?.temporaryDriverId);
      setValue('actualEndDate', dayjs.unix(row?.estimatedEndTime));
      const driverBreakHour: any = driverOptionData?.find(
        (item: any) => item?.id === row?.temporaryDriverId
      );
      setBreakHour(driverBreakHour);
      setActualToDate(dayjs.unix(row?.estimatedEndTime));

      setWaypoints(
        row?.waypointDetails?.map((item: any, index: number) => ({
          ...item,
          waypointName: `waypoint${index + 1}`
        }))
      );
      setSourceAddress({
        geometry: {
          location: {
            lat: () => {
              return row?.startLatitude;
            },
            lng: () => {
              return row?.startLongitude;
            }
          }
        },
        formatted_address: row?.source
      });
      setDestinationAddress({
        geometry: {
          location: {
            lat: () => {
              return row?.endLatitude;
            },
            lng: () => {
              return row?.endLongitude;
            }
          }
        },
        formatted_address: row?.destination
      });
      const previewPayload: PreviewTrip = {
        vehicleNumber: row?.vehicleNumber,
        source: row?.source,
        startLat: row?.startLatitude,
        startLng: row?.startLongitude,
        destination: row?.destination,
        endLat: row?.endLatitude,
        endLng: row?.endLongitude,
        waypointDetails: row?.waypointDetails
      };
      dispatch(previewTripAction(previewPayload));
      setTripMarker([
        {
          id: 'source',
          lat: row?.startLatitude,
          lng: row?.startLongitude,
          name: 'trip',
          info: { Mylocation: 'source' }
        },
        {
          id: 'destination',
          lat: row?.endLatitude,
          lng: row?.endLongitude,
          name: 'trip',
          info: { Mylocation: 'Destination' }
        }
      ]);
      setFromDate(row?.tripID && (dayjs as any).unix(row?.startTimestamp).$d);
      setToDate(row?.tripID && (dayjs as any).unix(row?.endTimestamp).$d);
    } else if (row?.length === 0) {
      setSourceAddress(null);
      setDestinationAddress(null);
      setValue('vehicleNumber', '');
      setValue('driverId', '');
      clearErrors('startDate');
      clearErrors('endDate');
    }
  }, [row]);

  useEffect(() => {
    if (
      fromDate &&
      toDate &&
      fromDate != 'Invalid Date' &&
      toDate != 'Invalid Date' &&
      Boolean(getValues('vehicleNumber'))
    ) {
      const startTimestamp = convertDatetoEpoch(fromDate) / 1000;
      const endTimestamp = convertDatetoEpoch(toDate) / 1000;
      const vehicleNum = getValues('vehicleNumber');
      if (vehicleNum !== '' && endTimestamp >= startTimestamp) {
        dispatch(
          getDriverslist({
            tripStart: startTimestamp,
            tripEnd: endTimestamp,
            vehicleNumber: vehicleNum,
            tripId: row?.tripID
          })
        );
      }
    }
  }, [fromDate, toDate, getValues('vehicleNumber')]);
  useEffect(() => {
    const isDestination = getValues('destinationAddress')?.destination;
    setValue('sourceAddress', {
      source: sourceAddress?.formatted_address,
      startLat: sourceAddress?.geometry?.location?.lat(),
      startLng: sourceAddress?.geometry?.location?.lng()
    });
    clearErrors('sourceAddress');
    const prevMarkers = tripMarker;

    if (prevMarkers?.length <= 1 && sourceAddress) {
      if (!Boolean(isDestination)) {
        setTripMarker([
          {
            id: 'source',
            lat: sourceAddress?.geometry?.location?.lat(),
            lng: sourceAddress?.geometry?.location?.lng(),
            name: 'trip',
            info: { Mylocation: 'source' }
          }
        ]);
      } else {
        setTripMarker([
          ...tripMarker,
          {
            id: 'source',
            lat: sourceAddress?.geometry?.location?.lat(),
            lng: sourceAddress?.geometry?.location?.lng(),
            name: 'trip',
            info: { Mylocation: 'source' }
          }
        ]);
      }
    } else if (sourceAddress) {
      setTripMarker((prev: any) => {
        let updatedMarkers = prev?.map((marker: any) =>
          marker?.id === 'source'
            ? {
                ...marker,
                lat: sourceAddress?.geometry?.location?.lat(),
                lng: sourceAddress?.geometry?.location?.lng()
              }
            : marker
        );
        return updatedMarkers;
      });
    }
  }, [sourceAddress]);

  useEffect(() => {
    setValue('destinationAddress', {
      destination: destinationAddress?.formatted_address,
      endLat: destinationAddress?.geometry?.location?.lat(),
      endLng: destinationAddress?.geometry?.location?.lng()
    });
    clearErrors('destinationAddress');
    const prevMarkers = tripMarker;
    const isDestinationMarker = prevMarkers?.some(
      (marker: any) => marker.id === 'destination'
    );
    if (destinationAddress?.geometry?.location?.lat()) {
      let lat = destinationAddress?.geometry?.location?.lat();
      let lng = destinationAddress?.geometry?.location?.lng();
      let destinationMarker = {
        id: 'destination',
        lat: lat,
        lng: lng,
        name: 'trip',
        info: { Mylocation: 'Destination' }
      };
      if (!isDestinationMarker) {
        prevMarkers.push(destinationMarker);
      } else {
        setTripMarker((marker: any) => {
          let updatedMarker = marker?.map((item: any) =>
            item?.id === 'destination'
              ? {
                  ...destinationMarker
                }
              : item
          );
          return updatedMarker;
        });
      }
    } else if (isDestinationMarker && destinationAddress) {
      setTripMarker((marker: any) => {
        let updatedMarkers = marker?.filter((item: any) => item?.id !== 'destination');
        return updatedMarkers;
      });
    }
  }, [destinationAddress]);

  useEffect(() => {
    setValue('waypointDetails', waypoints);

    const [sourceMarker] = tripMarker?.filter((marker: any) => marker?.id === 'source');

    const [destinationMarker] = tripMarker?.filter(
      (marker: any) => marker?.id === 'destination'
    );

    if (waypoints?.length > 0) {
      const waypointMarkers = waypoints
        ?.filter((item: any) => item?.waypointLat !== 0)
        .map((waypoint: any, index: number) => ({
          id: `waypoint${index + 1}`,
          lat: waypoint?.waypointLat,
          lng: waypoint?.waypointLng,
          name: 'waypoints',
          info: { Mylocation: `Waypoint ${index + 1}` }
        }));
      if (sourceMarker && destinationMarker && waypointMarkers?.length > 0) {
        setTripMarker([sourceMarker, ...waypointMarkers, destinationMarker]);
      } else if (sourceMarker && destinationMarker) {
        setTripMarker([sourceMarker, destinationMarker]);
      } else {
        setTripMarker(waypointMarkers);
      }
    } else if (sourceMarker && destinationMarker) {
      setTripMarker([sourceMarker, destinationMarker]);
    }
  }, [waypoints]);

  useEffect(() => {
    if (previewTripData) {
      const decoded = decode(previewTripData?.encodedPolyLine, 5)?.map(([lat, lng]) => ({
        lat,
        lng
      }));
      dispatch(
        updatePolyline([
          {
            id: 'actual',
            color: '#0f53ff',
            path: decoded
          }
        ])
      );
    }
  }, [previewTripData]);

  useEffect(() => {
    const waypoint: any = [];
    tripMarker?.forEach((item: any) => {
      if (item?.lat || item?.lng) {
        if (item?.id !== 'source' && item?.id !== 'destination') {
          const address = findAddress(item?.lat, item?.lng);
          address.then(res => {
            waypoint.push({
              waypointAddress: res,
              waypointLat: item?.lat,
              waypointLng: item?.lng,
              waypointColor: '#19A7CE',
              waypointRadius: 1
            });
            // setValue('waypointDetails', waypoint);
            return res;
          });
        } else if (item?.id === 'source') {
          const address = findAddress(item?.lat, item?.lng);
          address.then((res: any) => {
            setValue('sourceAddress', {
              source: res,
              startLat: item?.lat,
              startLng: item?.lng
            });
            setSource(res);
          });
        } else if (item?.id === 'destination') {
          const address = findAddress(item?.lat, item?.lng);
          address.then((res: any) => {
            setValue('destinationAddress', {
              destination: res,
              endLat: item?.lat,
              endLng: item?.lng
            });
            setDestination(res);
          });
        }
      }
    });
  }, [tripMarker, sourceAddress, destinationAddress]);

  useEffect(() => {
    dispatch(driverAction());
    return () => {
      handleClose();
      dispatch(clearPreview());
    };
  }, []);

  useEffect(() => {
    if (tripMarker[0]?.lat && tripMarker[0]?.lng) {
      dispatch(updateCenter(tripMarker));
      dispatch(updateZoom(tripMarker));
    }
  }, [tripMarker, tripMarker?.length]);

  useEffect(() => {
    if (paths?.length > 0) {
      setIsShowCalendar(true);
    }
  }, [paths]);

  useEffect(() => {
    if (previewTripData) {
      setMinDateTime(
        previewTripData?.totalDurationInSeconds &&
          dayjs(
            new Date(fromDate).getTime() +
              1000 * (previewTripData?.totalDurationInSeconds + 600)
          )
      );
      setMaxDateTime(
        previewTripData?.totalDurationInSeconds &&
          dayjs(
            new Date(fromDate).getTime() +
              1000 * (previewTripData?.totalDurationInSeconds + 3600)
          )
      );
    }
  }, [previewTripData, fromDate]);

  useEffect(() => {
    const driverDropdown =
      (driverData as any)?.length > 0
        ? (driverData as any)?.map((value: any) => ({
            id: value?.driverId,
            label: `${value?.driverName?.toUpperCase()}${
              value?.totalBreakDuration ? `(${value?.totalBreakDuration})` : ''
            }`,
            break: value?.totalBreakDuration
          }))
        : [];
    setDriverOptionData(driverDropdown);
  }, [driverData]);

  useEffect(() => {
    if (fields?.length > 0) {
      const isValue = fields?.every(
        (item: any) => item.waypointLat && item.waypointLat !== 0
      );
      setIsWayPoint(isValue);
    }
  }, [fields]);

  return (
    <>
      <Box className='add-trip'>
        <Dialog
          open={isOpen}
          fullScreen
          sx={{ padding: 3, zIndex: 1, position: 'absolute' }}
          className='add-trip-dialog animate__animated animate__zoomInRight'
          BackdropProps={{
            invisible: true
          }}
        >
          <DialogContent
            sx={{ overflow: `${isMatchesBelowSM ? 'scroll' : 'hidden'}`, padding: 1 }}
            className='add-trip-dialog-content'
          >
            <Grid
              container
              sx={{
                flexDirection: { xs: 'column-reverse', md: 'row', sm: 'row' }
              }}
              className='add-trip-grid-container'
            >
              <Grid item xs={12} md={8} sm={8} lg={8.7} sx={{ overflow: 'hidden' }} p={1}>
                <Box
                  sx={{
                    width: '100%',
                    height: '86vh',
                    overflow: 'hidden',
                    borderRadius: '10px'
                  }}
                >
                  <GoogleMap>
                    {paths?.length > 0 && <GooglePolyline />}
                    {tripMarker?.length > 0 && (
                      <GoogleTripDirections
                        locations={tripMarker}
                        waypoints={waypoints}
                        origin={sourceAddress}
                        destination={destinationAddress}
                        isDraggable={true}
                        setWaypoints={setWaypoints}
                        setTripMarker={setTripMarker}
                        handleMarker={handleMarkerDrag}
                        clearStartAndEndDate={clearStartAndEndDate}
                      />
                    )}
                  </GoogleMap>
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
                sm={4}
                lg={3.3}
                px={1}
                sx={{
                  height: 'fit-content',
                  maxHeight: `${isMatchesBelowSM ? 'none' : '86vh'}`,
                  overflowY: 'auto'
                }}
                className='add-trip-grid-item'
              >
                <Box className='add-trip-header'>
                  <Typography className='add-trip-title'>
                    {row?.tripID ? constant.UpdateTrip : 'Add New Trip'}
                  </Typography>
                </Box>
                <Box
                  className='closeIcon icon-position d-flex close-icon-index'
                  onClick={() => handleClose()}
                >
                  <CustomIconButton category='CloseValue' />
                </Box>
                <Box
                  className='add-trip-form animate__animated animate__zoomIn animate__slow'
                  component='form'
                  onSubmit={handleSubmit(onsubmit)}
                >
                  <CustomSelect
                    id='vehicleNumber'
                    control={control}
                    label='Select Vehicle'
                    placeholder='Select vehicle'
                    options={row?.tripID ? UpdateVehicleList : vehicleList}
                    name='vehicleNumber'
                    onChanges={(e: any, newValue: any) => {
                      setValue('driverId', '');
                      setValue('actualEndDate', {});
                      setActualToDate(null);
                      setDriverOptionData([]);
                      setValue('vehicleNumber', newValue?.id);
                      // trigger('vehicleNumber');
                    }}
                  />

                  <GoogleSearchBox
                    id='search-source'
                    name='sourceAddress'
                    control={control}
                    label='Source'
                    placeholder='Source address'
                    value={source || sourceAddress?.formatted_address}
                    onchange={(data: any) => {
                      if (!data?.target?.value) {
                        resetField('sourceAddress');
                        setTripMarker((prev: any) => {
                          let newMarker = prev?.filter(
                            (item: any) => item?.id !== 'source'
                          );
                          return newMarker;
                        });
                        setSourceAddress(null);
                      }
                      clearStartAndEndDate();
                    }}
                    setAddress={setSourceAddress}
                  />

                  {/* <Box my={2} /> */}
                  <GoogleSearchBox
                    id='search-destination'
                    name='destinationAddress'
                    control={control}
                    label='Destination'
                    placeholder='Destination address'
                    value={destination || destinationAddress?.formatted_address}
                    onchange={(data: any) => {
                      if (!data?.target?.value) {
                        resetField('destinationAddress');
                        setTripMarker((prev: any) => {
                          let newMarker = prev?.filter(
                            (item: any) => item?.id !== 'destination'
                          );
                          return newMarker;
                        });
                        setDestinationAddress(null);
                      }
                      clearStartAndEndDate();
                    }}
                    setAddress={setDestinationAddress}
                  />

                  {fields?.map((field: any, index) => (
                    <Grid
                      container
                      key={field.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginTop: '10px'
                      }}
                    >
                      <Grid item xs={11}>
                        <GoogleSearchBox
                          key={field.id}
                          id={`waypoint${index + 1}`}
                          name={`waypointDetails.${index}.waypointAddress`}
                          control={control}
                          label={`Waypoint ${index + 1}`}
                          placeholder={`Waypoint ${index + 1}`}
                          value={getValues(`waypointDetails.${index}.waypointAddress`)}
                          onChangeCallback={(e: any) => {
                            let prevWaypoints: any = [...waypoints];
                            prevWaypoints[index] = {
                              waypointName: `waypoint${index + 1}`,
                              waypointLat: e?.geometry?.location?.lat(),
                              waypointLng: e?.geometry?.location?.lng(),
                              waypointAddress: e?.formatted_address,
                              waypointColor: '#19A7CE',
                              waypointRadius: 1
                            };
                            setWaypoints(prevWaypoints);
                            clearErrors(`waypointDetails.${index}.waypointAddress`);
                          }}
                          onchange={(e: any) => {
                            clearStartAndEndDate();
                            if (!e.target.value) {
                              setTripMarker((prev: any) => {
                                const updatedMarkers = prev.filter(
                                  (item: any) => item.id !== field.waypointName
                                );
                                return updatedMarkers;
                              });
                              setWaypoints((waypoint: any) => {
                                const updatedWaypoints = waypoint?.map((item: any) => {
                                  if (item.waypointName === field.waypointName) {
                                    return {
                                      waypointAddress: '',
                                      waypointLat: 0,
                                      waypointLng: 0,
                                      waypointColor: '#19A7CE',
                                      waypointRadius: 1
                                    };
                                  } else return item;
                                });
                                return updatedWaypoints;
                              });
                            }
                          }}
                        />
                      </Grid>

                      <Grid item xs={1} pb={1} marginTop={'18px'}>
                        <CustomIconButton
                          category='Delete'
                          onClick={() => {
                            setWaypointDisable(false);
                            let newWayPoints = [...waypoints];
                            remove(index);
                            newWayPoints?.splice(index, 1);
                            setWaypoints(newWayPoints);
                            clearStartAndEndDate();
                          }}
                        />
                      </Grid>
                    </Grid>
                  ))}

                  {/* <Box marginBottom={'10px'} /> */}
                  <Grid container spacing={1}>
                    <Grid item md={6} sm={12}>
                      <CustomButton
                        className='custom-button-another'
                        category='Add Waypoint'
                        variant='outlined'
                        fullWidth
                        disabled={waypointDisable}
                        startIcon={<AddCircleOutline />}
                        onClick={() => {
                          if (fields?.length === 4) {
                            setWaypointDisable(true);
                          }
                          setWaypoints((waypoint: any) => [
                            ...waypoint,
                            {
                              waypointAddress: '',
                              waypointLat: 0,
                              waypointLng: 0,
                              waypointColor: '#19A7CE',
                              waypointRadius: 1
                            }
                          ]);
                          append({
                            waypointAddress: '',
                            waypointLat: 0,
                            waypointLng: 0,
                            waypointColor: '#19A7CE',
                            waypointRadius: 1
                          });
                        }}
                      />
                    </Grid>

                    <Grid item md={6} sm={12}>
                      <CustomButton
                        className='custom-button-another'
                        category='Preview'
                        variant='outlined'
                        fullWidth
                        endIcon={<Visibility />}
                        disabled={
                          !(
                            sourceAddress &&
                            destinationAddress &&
                            (fields?.length > 0 ? isWayPoint : true)
                          )
                        }
                        onClick={getPreview}
                        loading={isLoadingPreview}
                      />
                    </Grid>
                  </Grid>

                  <Box marginBottom={'10px'} />
                  <CustomDateTimeCalendar
                    id='select-startdate'
                    name='startDate'
                    control={control}
                    label='Start Date & Time'
                    disableFuture={false}
                    disablePast={true}
                    format='DD/MM/YYYY hh:mm a'
                    placeholder='DD/MM/YYYY HH:MM'
                    value={fromDate}
                    isDisabled={!(sourceAddress && destinationAddress) || !isShowCalendar}
                    onDateChange={(e: any) => {
                      setValue('driverId', '');
                      setDriverOptionData([]);
                      if (e?.$d && e?.$d != 'Invalid Date') {
                        if (toDate) {
                          setValue('endDate', {});
                          clearErrors('endDate');
                          setToDate(null);
                        }
                        setFromDate(e?.$d);
                        setToDate(
                          previewTripData?.totalDurationInSeconds &&
                            dayjs(
                              new Date(e).getTime() +
                                1000 * (previewTripData?.totalDurationInSeconds + 600)
                            )
                        );
                        previewTripData?.totalDurationInSeconds &&
                          setValue(
                            'endDate',
                            dayjs(
                              new Date(e).getTime() +
                                1000 * (previewTripData?.totalDurationInSeconds + 600)
                            )
                          );
                        clearErrors('endDate');

                        setMinDateTime(
                          previewTripData?.totalDurationInSeconds &&
                            dayjs(
                              new Date(e).getTime() +
                                1000 * (previewTripData?.totalDurationInSeconds + 600)
                            )
                        );
                        setMaxDateTime(
                          previewTripData?.totalDurationInSeconds &&
                            dayjs(
                              new Date(e).getTime() +
                                1000 * (previewTripData?.totalDurationInSeconds + 3600)
                            )
                        );
                        setValue(
                          'startDate',
                          dayjs.unix(convertDatetoEpoch(e?.$d) / 1000)
                        );
                        clearErrors('startDate');
                      } else {
                        setFromDate(null);
                        setToDate(null);
                        setValue('startDate', null);
                        setValue('endDate', null);
                      }
                      setValue('actualEndDate', {});
                      setActualToDate(null);
                      setDriverOptionData([]);
                    }}
                    minDateTime={dayjs()
                      .set('hour', new Date().getHours())
                      .set('minutes', new Date().getMinutes() + 10)
                      .startOf('minute')}
                    onAccept={e => {
                      let selectedDateTime =
                        new Date() > new Date(e?.$d)
                          ? new Date().setMinutes(new Date().getMinutes() + 10)
                          : e?.$d;
                      setMinDateTime(
                        previewTripData?.totalDurationInSeconds &&
                          dayjs(
                            new Date(selectedDateTime).getTime() +
                              1000 * (previewTripData?.totalDurationInSeconds + 600)
                          )
                      );
                      setMaxDateTime(
                        previewTripData?.totalDurationInSeconds &&
                          dayjs(
                            new Date(selectedDateTime).getTime() +
                              1000 * (previewTripData?.totalDurationInSeconds + 3600)
                          )
                      );
                      // setValue('startDate', dayjs.unix(selectedDateTime / 1000));
                      clearErrors('startDate');
                      setValue('actualEndDate', {});
                      setActualToDate(null);
                    }}
                  />

                  {/* <Box my={2} /> */}
                  <CustomDateTimeCalendar
                    id='select-enddate'
                    name='endDate'
                    label='End Date & Time'
                    control={control}
                    disableFuture={false}
                    disablePast={true}
                    format='DD/MM/YYYY hh:mm a'
                    placeholder='DD/MM/YYYY HH:MM'
                    value={toDate}
                    isDisabled={!(sourceAddress && destinationAddress) || !isShowCalendar}
                    onDateChange={(e: any) => {
                      setValue('driverId', '');
                      setDriverOptionData([]);
                      if (e?.$d && e?.$d != 'Invalid Date') {
                        setToDate(e?.$d);
                        setValue('endDate', dayjs.unix(convertDatetoEpoch(e?.$d) / 1000));
                        clearErrors('endDate');
                        trigger('endDate');
                        // actualEndTime(e?.$d, breakHour);
                      } else {
                        setValue('actualEndDate', {});
                        setActualToDate(null);
                        setToDate(null);
                        setValue('endDate', null);
                      }
                      setValue('actualEndDate', {});
                      setActualToDate(null);
                    }}
                    onAccept={(e: any) => {
                      let selectedDateTime =
                        new Date(minDateTime) > new Date(e?.$d)
                          ? new Date(minDateTime)
                          : e?.$d;
                      setValue(
                        'endDate',
                        dayjs.unix(convertDatetoEpoch(selectedDateTime) / 1000)
                      );
                      clearErrors('endDate');
                      // actualEndTime(selectedDateTime, breakHour);
                    }}
                    minDateTime={minDateTime}
                    maxDateTime={maxDateTime}
                  />

                  {/* <Box my={2} /> */}
                  <CustomSelect
                    id='driverId'
                    control={control}
                    key={getValues('driverId')}
                    label='Select Driver'
                    placeholder='Select Driver'
                    options={driverOptionData}
                    name='driverId'
                    // isDisabled={!(sourceAddress && destinationAddress) || !isShowCalendar}
                    isDisabled={
                      getValues('startDate') &&
                      getValues('endDate') &&
                      getValues('endDate') >= getValues('startDate') &&
                      getValues('vehicleNumber')
                        ? false
                        : true
                    }
                    value={driverDropdown.find(
                      (driver: { temporaryDriverId: any }) =>
                        driver.temporaryDriverId === getValues('driverId')
                    )}
                    onChange={(selectedOption: any) => {
                      if (selectedOption) {
                        clearErrors('driverId');
                      }
                    }}
                    onChanges={(e: any, newValue: any) => {
                      if (newValue) {
                        setBreakHour(newValue);
                        actualEndTime(toDate, newValue);
                        trigger('actualEndDate');
                      } else {
                        setActualToDate(null);
                        setValue('actualEndDate', {});
                      }
                    }}
                  />
                  {/* <Box mt={2}> */}
                  <CustomDateTimeCalendar
                    id='select-actualenddate'
                    name='actualEndDate'
                    label='Actual End Date & Time'
                    control={control}
                    disableFuture={false}
                    disablePast={true}
                    format='DD/MM/YYYY hh:mm a'
                    placeholder='DD/MM/YYYY HH:MM'
                    value={actualToDate}
                    isDisabled={true}
                    onDateChange={(e: any) => {
                      if (e) {
                        setToDate(e?.$d);
                        setValue(
                          'actualEndDate',
                          dayjs.unix(convertDatetoEpoch(e?.$d) / 1000)
                        );
                        clearErrors('actualEndDate');
                        actualEndTime(e?.$d, breakHour);
                      }
                    }}
                    onAccept={(e: any) => {
                      setToDate(e?.$d);
                      setValue(
                        'actualEndDate',
                        dayjs.unix(convertDatetoEpoch(e?.$d) / 1000)
                      );
                      clearErrors('actualEndDate');
                      actualEndTime(e?.$d, breakHour);
                    }}
                  />
                  {/* </Box> */}
                  <Box mt={3}>
                    <CustomButton
                      category={row?.tripID ? 'Update Trip' : 'Add New Trip'}
                      className='saveChanges'
                      loading={updateData?.isLoading || addData?.isLoading}
                      type='submit'
                      sx={{ marginRight: '10px' }}
                    />

                    <CustomButton
                      category='Cancel'
                      className='cancel'
                      onClick={() => handleClose()}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
        </Dialog>
      </Box>
      {children}
    </>
  );
};

export default AddTrip;
